import { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/api";
import { Plus, Trash2, X, ShieldCheck, UserCog, History, Edit2, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_META = {
  admin: { label: "Yönetici", color: "success" },
  employee: { label: "Çalışan", color: "info" },
};

function UserFormModal({ initial, onClose, onSaved }) {
  const editing = !!initial;
  const [form, setForm] = useState({
    email: initial?.email || "",
    password: "",
    name: initial?.name || "",
    position: initial?.position || "",
    role: initial?.role || "employee",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      if (editing) {
        const payload = { name: form.name, position: form.position, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${initial.id}`, payload);
        toast.success("Kullanıcı güncellendi");
      } else {
        await api.post("/users", form);
        toast.success("Kullanıcı oluşturuldu");
      }
      onSaved();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, editing ? "Güncellenemedi" : "Kullanıcı oluşturulamadı"));
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="user-modal">
      <form className="modal" onSubmit={submit}>
        <button type="button" className="modal-close" onClick={onClose}><X size={18}/></button>
        <p className="eyebrow">Kullanıcı yönetimi</p>
        <h2>{editing ? "Kullanıcıyı düzenle" : "Yeni kullanıcı ekle"}</h2>
        <p className="subheading">
          {editing
            ? "Bilgileri güncelleyin veya yeni bir parola atayın. Parolayı boş bırakırsanız değişmez."
            : "Yeni çalışan için hesap oluşturun — oluşturduğunuz e-posta ve geçici parolayı kendisine iletin."}
        </p>
        <label>Ad soyad
          <input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required data-testid="user-name-input"/>
        </label>
        <label>Pozisyon <span className="cell-muted" style={{fontSize:10}}>(örn: Pazarlama Uzmanı)</span>
          <input value={form.position} onChange={(e)=>setForm({...form, position: e.target.value})} data-testid="user-position-input"/>
        </label>
        {!editing && (
          <label>E-posta
            <input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} required data-testid="user-email-input"/>
          </label>
        )}
        <label>{editing ? "Yeni parola" : "Geçici parola"} <span className="cell-muted" style={{fontSize:10}}>(en az 6 karakter{editing ? ", boş bırakılabilir" : ""})</span>
          <input type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} required={!editing} minLength={editing ? undefined : 6} data-testid="user-password-input"/>
        </label>
        <label>Rol
          <select value={form.role} onChange={(e)=>setForm({...form, role: e.target.value})} data-testid="user-role-select">
            <option value="employee">Çalışan — Rapor yazar, ekip raporlarını görür</option>
            <option value="admin">Yönetici — Tam yetki, kullanıcı yönetir</option>
          </select>
        </label>
        {error && <div className="form-error" data-testid="user-form-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="ghost-button" onClick={onClose} data-testid="user-cancel">İptal</button>
          <button type="submit" className="primary-button" disabled={saving} data-testid="user-submit">
            <Plus size={16}/> {saving ? "Kaydediliyor…" : (editing ? "Güncelle" : "Oluştur")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function UsersPage({ onViewHistory }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Hesap devre dışı bırakıldı" : "Hesap etkinleştirildi");
      load();
    } catch { toast.error("İşlem başarısız"); }
  };

  const removeUser = async (u) => {
    if (u.id === me.id) { toast.error("Kendi hesabınızı silemezsiniz"); return; }
    if (!window.confirm(`${u.name} silinsin mi? Tüm raporları da silinecek.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      load();
    } catch { toast.error("Silinemedi"); }
  };

  return (
    <>
      <div className="page-heading compact-heading">
        <div>
          <p className="eyebrow">Yönetim</p>
          <h1>Kullanıcılar</h1>
          <p className="subheading">Çalışan hesaplarını oluşturun, parola sıfırlayın, devre dışı bırakın.</p>
        </div>
        <div className="heading-actions">
          <button className="primary-button" onClick={()=>{setEditing(null); setShowForm(true);}} data-testid="add-user-btn">
            <Plus size={16}/> Yeni kullanıcı
          </button>
        </div>
      </div>

      <div className="panel table-panel full-table" style={{ marginTop: 24 }}>
        {loading ? <div className="loading-block">Yükleniyor…</div> :
          users.length === 0 ? (
            <div className="empty-state">
              <UserCog size={28}/>
              <h3>Henüz kullanıcı yok</h3>
            </div>
          ) : (
            <div className="data-table-wrap">
              <table data-testid="users-table">
                <thead><tr>
                  <th>Ad soyad</th><th>Pozisyon</th><th>E-posta</th><th>Rol</th><th>Durum</th><th>Eklenme</th><th className="right">İşlem</th>
                </tr></thead>
                <tbody>
                  {users.map((u) => {
                    const meta = ROLE_META[u.role] || ROLE_META.employee;
                    return (
                      <tr key={u.id} data-testid={`user-row-${u.id}`}>
                        <td className="bold-cell">
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                            <span className="chip-avatar">{u.name.charAt(0)}</span>
                            {u.name}
                            {u.id === me.id && <small style={{ color: "#8390a5", marginLeft: 4 }}>(siz)</small>}
                          </span>
                        </td>
                        <td>{u.position || <span className="cell-muted">—</span>}</td>
                        <td className="mono" style={{ fontSize: 12 }}>{u.email}</td>
                        <td><span className={`badge ${meta.color}`}>{meta.label}</span></td>
                        <td>
                          {u.active === false
                            ? <span className="badge danger">Devre dışı</span>
                            : <span className="badge success">Aktif</span>}
                        </td>
                        <td style={{ fontSize: 12, color: "#8390a5" }}>{formatDate(u.created_at)}</td>
                        <td className="right actions-cell">
                          {u.role !== "admin" && (
                            <button className="icon-btn" onClick={()=>onViewHistory?.(u)} title="Rapor geçmişi" data-testid={`view-history-${u.id}`}>
                              <History size={14}/>
                            </button>
                          )}
                          <button className="icon-btn" onClick={()=>{setEditing(u); setShowForm(true);}} title="Düzenle" data-testid={`edit-user-${u.id}`}>
                            <Edit2 size={14}/>
                          </button>
                          {u.id !== me.id && (
                            <>
                              <button className="icon-btn" onClick={()=>toggleActive(u)} title={u.active === false ? "Etkinleştir" : "Devre dışı bırak"} data-testid={`toggle-active-${u.id}`}>
                                {u.active === false ? <Power size={14}/> : <PowerOff size={14}/>}
                              </button>
                              <button className="icon-btn danger" onClick={()=>removeUser(u)} title="Sil" data-testid={`delete-user-${u.id}`}>
                                <Trash2 size={14}/>
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {showForm && (
        <UserFormModal
          initial={editing}
          onClose={()=>setShowForm(false)}
          onSaved={()=>{ setShowForm(false); load(); }}
        />
      )}
    </>
  );
}
